import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { OpenclawSettingsTabs } from "@/components/settings/OpenclawSettingsTabs";

/**
 * `ConfigDialog` — the gear (masterplan §3.4, S4). What REMAINS of the
 * Configurações tab after everything settings-near-function moved to the thing
 * it configures: channels → the card chips' per-channel dialogs, identity →
 * the hero pencils, Agendamentos → its own rail pane. The residue —
 * Pessoal · Notificações · Voz (interim, until the S6 hero chip) ·
 * Organização — is genuinely cross-cutting, so it lives behind one gear
 * instead of a permanent tab spending rail width.
 *
 * It REHOSTS `OpenclawSettingsTabs` unchanged: same loader, same
 * `OpenclawSettingsContext`, same RBAC filter, same kill-switch propagation,
 * same testids. The tab catalog already filters itself down to the residue
 * (Identidades/Canais/Agendamentos were retired from it in earlier slices), so
 * the dialog needs no variant prop — moving the shell into a modal is the
 * whole change, and nothing about the write paths moves with it.
 */
export function ConfigDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  // Esc closes THIS dialog only (capture + stopImmediatePropagation), so it
  // never also reaches FocusPaneHost and closes the pane underneath.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="config-dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("agent.sections.settings")}
        className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-coffee-900"
      >
        <div className="flex shrink-0 items-start gap-2 border-b border-cream-200/70 px-5 py-4 dark:border-coffee-800">
          <div className="min-w-0 flex-1">
            <h2 className="font-serif-display text-lg text-coffee-900 dark:text-cream-100">
              {t("agent.sections.settings")}
            </h2>
            <p className="mt-0.5 text-body-sm text-coffee-600 dark:text-cream-400">
              {t("agent.configDialog.blurb")}
            </p>
          </div>
          <button
            type="button"
            data-testid="config-dialog-close"
            onClick={onClose}
            aria-label={t("common.close")}
            title={t("common.close")}
            className="shrink-0 rounded-lg p-1.5 text-coffee-500 transition-colors hover:bg-cream-100 hover:text-coffee-800 dark:text-cream-500 dark:hover:bg-coffee-800 dark:hover:text-cream-100"
          >
            <X size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <OpenclawSettingsTabs />
        </div>
      </div>
    </div>
  );
}
